/**
 * AgencyApp.jsx — minimal agency-teammate shell.
 * Mounted at /agency/* for users with agency_role set.
 *
 * Sidebar: Engagements · Intro Calls · Sign out.
 * Sprint 5 will rebuild this as the full Agency Command Centre (Module 08).
 */
import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Briefcase, SignOut, Buildings, Kanban, ChartLine, UsersThree, Gear } from '@phosphor-icons/react';
import AgencyHome from './AgencyHome';
import AgencyPipeline from './AgencyPipeline';
import AgencyRevenue from './AgencyRevenue';
import AgencyTeam from './AgencyTeam';
import AgencySettings from './AgencySettings';
import EngagementWorkspace from './EngagementWorkspace';
import api from '@/api';

const ACCENT = '#6366f1';

const AgencyApp = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [agency, setAgency] = useState(null);

  useEffect(() => {
    api.get('/agencies/me')
      .then((r) => setAgency(r.data.agency))
      .catch(() => {});
  }, []);

  // (Iter 52) Agencies enter via /studio/login — return them there on logout
  // so they don't bounce to the founder login page.
  const logout = () => { localStorage.removeItem('token'); localStorage.removeItem('user'); setUser(null); navigate('/studio/login'); };

  const NAV = [
    { path: '/agency',          label: 'Engagements', icon: Briefcase,  testid: 'agency-nav-home' },
    { path: '/agency/pipeline', label: 'Pipeline',    icon: Kanban,     testid: 'agency-nav-pipeline' },
    { path: '/agency/revenue',  label: 'Revenue',     icon: ChartLine,  testid: 'agency-nav-revenue' },
    { path: '/agency/team',     label: 'Team',        icon: UsersThree, testid: 'agency-nav-team' },
    { path: '/agency/settings', label: 'Settings',    icon: Gear,       testid: 'agency-nav-settings' },
  ];

  return (
    <div className="flex min-h-screen bg-background text-text-primary" data-testid="agency-app">
      <aside className="w-56 border-r border-border flex flex-col">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT}aa)` }}>
            <Buildings size={16} weight="fill" className="text-white" />
          </span>
          <div>
            <div className="text-[9px] uppercase tracking-[0.24em] font-bold" style={{ color: ACCENT }}>Founderboard</div>
            <div className="text-sm font-heading font-bold text-text-primary leading-none">Agency</div>
          </div>
        </div>
        <div className="px-5 py-3 border-b border-border">
          <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Agency</div>
          <div className="text-sm font-medium text-text-primary truncate">{agency?.name || '—'}</div>
          <div className="text-[10px] text-text-muted capitalize mt-0.5">{user?.agency_role} · {user?.full_name}</div>
        </div>
        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = location.pathname === n.path || (n.path !== '/agency' && location.pathname.startsWith(n.path));
            return (
              <Link key={n.path} to={n.path} data-testid={n.testid} className={`flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors ${active ? 'bg-surface text-text-primary' : 'text-text-secondary hover:bg-surface/60 hover:text-text-primary'}`}>
                <Icon size={14} weight="bold" /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="px-2 pb-3">
          <button onClick={logout} data-testid="agency-logout" className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-danger hover:bg-danger/10 rounded-lg">
            <SignOut size={14} weight="bold" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <Routes>
          <Route path="/" element={<AgencyHome agency={agency} />} />
          <Route path="/pipeline" element={<AgencyPipeline />} />
          <Route path="/revenue" element={<AgencyRevenue />} />
          <Route path="/team" element={<AgencyTeam user={user} />} />
          <Route path="/settings" element={<AgencySettings user={user} />} />
          <Route path="/engagements/:engagementId" element={<EngagementWorkspace user={user} />} />
          <Route path="*" element={<Navigate to="/agency" replace />} />
        </Routes>
      </main>
    </div>
  );
};

export default AgencyApp;
